import TextField from '@mui/material/TextField'
import styles from '../styles/styles.module.css'
import SecondaryButton from '../../../../../../../global/components/buttons/secondary-button/SecondaryButton'
import PrimaryButton from '../../../../../../../global/components/buttons/primary-button/PrimaryButton'
import { useState, useContext } from 'react'
import { getErrors } from '../../../../../../../global/utils/formValidation'
import { fetchData } from '../../../../../../../global/utils/fetch'
import { changeAddress, doesOrderUpdateAffectScheduledJobs } from './api'
import { WoDetailContext } from '../../../../../context/context'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { ToastNotificationContext } from '../../../../../../../global/context/toast-context/ToastNotificationContext'
import { IInstallationPoint } from '../../../../../../../../../app/entities/InstallationPoint'
import { STATUSES } from '../../../../../../../global/constants/order-status'

interface IProps {
  handleClose: Function
}

const formOptions = {
  address: {
    label: 'Address',
    autoComplete: 'address-line1',
    required: true,
    validator: (val: any) => (val ? '' : 'Please include an address'),
  },
  aptOrSuite: {
    label: 'Apt/Suite',
    autoComplete: 'address-line2',
    validator: () => '',
  },
  city: {
    label: 'City',
    autoComplete: 'address-level2',
    required: true,
    validator: (val: any) => (val ? '' : 'Please include a city'),
  },
  state: {
    label: 'State',
    autoComplete: 'address-level1',
    required: true,
    validator: (val: any) => (val ? '' : 'Please include a state'),
  },
  zipCode: {
    label: 'Zip Code',
    autoComplete: 'postal-code',
    required: true,
    inputProps: { maxLength: 5 },
    validator: (val: any) => (val ? '' : 'Please include a zip code'),
  },
} as { [key: string]: any }

export default function ChangeAddress({ handleClose }: IProps) {
  const { queryOrderData, data: order } = useContext(WoDetailContext)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  const [form, setForm] = useState({
    address: '',
    aptOrSuite: '',
    city: '',
    state: '',
    zipCode: '',
  } as { [key: string]: any })

  const [isButtonDisabled, setIsButtonDisabled] = useState(false)

  function onChange(e: any) {
    const { name, value } = e.target
    setForm({ ...form, [name]: value })
  }

  async function handleAddressSave() {
    const errors = getErrors(form, formOptions)

    if (errors.length) {
      return alert(errors.join('\n'))
    }

    try {
      setIsButtonDisabled(true)
      const shouldCheckAddressForReschedule =
        order.status === STATUSES.scheduled // only check if the order is scheduled

      const orderAffectsScheduledJobs = await fetchData<boolean>(
        doesOrderUpdateAffectScheduledJobs(order.objectId, form),
      )

      const canMoveOrderWithoutRescheduling =
        !shouldCheckAddressForReschedule || !orderAffectsScheduledJobs

      if (!canMoveOrderWithoutRescheduling) {
        if (
          !window.confirm(
            'This change will require rescheduling the order. Are you sure you want to continue?',
          )
        ) {
          return
        }
      }

      await fetchData<IInstallationPoint>(
        changeAddress({ ...form, orderObjectId: order.objectId }),
      )

      await queryOrderData(order.objectId)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Successfully changed address!',
        severity: 'success',
      })
    } catch (err) {
      console.error(err)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'There was an error saving the address',
        severity: 'error',
      })
    } finally {
      setIsButtonDisabled(false)
    }
  }

  return (
    <div className={styles.inputsContainer}>
      {Object.keys(form).map((field) => (
        <div
          className={styles.topInputsContainer}
          key={field}
        >
          <TextField
            name={field}
            required={formOptions[field].required}
            autoComplete={formOptions[field].autoComplete}
            label={formOptions[field].label}
            inputProps={formOptions[field].inputProps}
            onChange={onChange}
          />
        </div>
      ))}
      <div className={styles.buttonContainer}>
        <SecondaryButton
          buttonName='Cancel'
          onClick={() => handleClose()}
        />
        <PrimaryButton
          buttonName='Save'
          onClick={handleAddressSave}
          disabled={isButtonDisabled}
        />
      </div>
    </div>
  )
}
